import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LegalFooter } from "@/components/LegalFooter";

/**
 * Terms & Conditions for Xenomons.
 * Covers Paddle readiness requirements: seller identification, acceptable use,
 * IP, MoR disclosure, withdrawal waiver for digital content, liability, law.
 * Review legally before going live.
 */
export const Route = createFileRoute("/agb")({
  head: () => ({
    meta: [
      { title: "Terms & Conditions / AGB – Xenomons" },
      {
        name: "description",
        content:
          "Terms & Conditions for Xenomons: contracting party, Gold purchases, Paddle as Merchant of Record, withdrawal, liability.",
      },
      { property: "og:title", content: "Terms & Conditions – Xenomons" },
      {
        property: "og:description",
        content: "Terms of use and purchase processing for Xenomons.",
      },
      { property: "og:url", content: "https://xenomons.online/agb" },
    ],
    links: [
      { rel: "canonical", href: "https://xenomons.online/agb" },
      { rel: "alternate", hrefLang: "de", href: "https://xenomons.online/agb" },
      { rel: "alternate", hrefLang: "en", href: "https://xenomons.online/agb" },
      { rel: "alternate", hrefLang: "x-default", href: "https://xenomons.online/agb" },
    ],
  }),
  component: AGB,
});

type Section = {
  title: string;
  paragraphs?: string[];
  items?: string[];
};

function AGB() {
  const { t } = useTranslation("legal");
  const sections = t("terms.sections", { returnObjects: true }) as Section[];

  return (
    <main className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 px-6 py-10">
        <div className="mx-auto max-w-2xl">
          <Link to="/" className="text-sm font-medium text-primary hover:underline">
            {t("back")}
          </Link>
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight">
            {t("terms.title")}
          </h1>
          <p className="mt-3 text-xs italic text-muted-foreground">
            {t("terms.status")}
          </p>

          <section className="mt-6 space-y-6 text-sm leading-relaxed">
            {sections.map((s, i) => (
              <div key={i}>
                <h2 className="text-lg font-bold">{s.title}</h2>
                {s.paragraphs?.map((p, j) => (
                  <p key={j} className={j === 0 ? "" : "mt-2"}>
                    {p}
                  </p>
                ))}
                {s.items && (
                  <ul className="list-disc pl-5 space-y-1 mt-2">
                    {s.items.map((it, k) => (
                      <li key={k}>{it}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </section>
        </div>
      </div>
      <LegalFooter />
    </main>
  );
}
